define('presentation/settings/schedules/viewModels/schedulesViewModel',[
    'businessServices/events/eventManager',
    'businessServices/router/router',
    'common/promises/promiseFactory',
    'common/collections/collectionFilter',
    'common/collections/collectionSorter',
    'common/storage/commonState',
    'constants/scrollEventSources',
    'presentation/common/actionModal/viewModels/actionModalViewModel',
    'presentation/common/actionModal/viewModels/deleteActionViewModel',
    'presentation/common/warningModal/viewModels/integrityWarningModalViewModel',
    'presentation/settings/schedules/facades/schedulesFacade',
    'presentation/settings/schedules/viewModels/scheduleCommonFunctions',
    'settings/navigationConfiguration'
], function () {
    return function () {
        const self = this;

        const PromiseFactoryConstructor = require('common/promises/promiseFactory');
        const _promiseFactory = new PromiseFactoryConstructor();

        const DeleteActionViewModelConstructor = require('presentation/common/actionModal/viewModels/deleteActionViewModel');
        const FilterConstructor = require('common/collections/collectionFilter');
        const SorterConstructor = require('common/collections/collectionSorter');

        const _commonState = require('common/storage/commonState');
        const _navigationConfiguration = require('settings/navigationConfiguration');
        const _router = require('businessServices/router/router');
        const _scheduleCommonFunctions = require('presentation/settings/schedules/viewModels/scheduleCommonFunctions');

        /** @type typeof import('constants/scrollEventSources') */
        const ScrollEventSources = require('constants/scrollEventSources');

        let _disposables = [];
        let _eventManager = null;
        let _facade = null;
        let _getNextBatchOfSchedulesEventId = null;
        let _getNextBatchCalled = false;

        const DELETE_RESPONSE = "yes";
        const ROWS_PER_PAGE = 15;
        const _totalQuarterHours = 96;

        let _state = {
            availableSchedules: [],
            filteredSchedules: [],
            schedulesShown: 0
        };

        class GridCell {
            constructor() {
                this.inRange = ko.observable();
                this.rangeColor = ko.pureComputed(() => {
                    const currentRange = this.inRange();
                    if (currentRange > 0) {
                        return `range-background__sequence-${currentRange}`;
                    } else {
                        return "range-background__sequence-all-other";
                    }
                });
            }
        }

        const _emptyGridCellsForDay = () => Array.from(new Array(_totalQuarterHours), () => new GridCell());

        const _rangeIndicatorClassNumber = (segmentsCount, segmentSequence) => {
            const classNumbers = [0,1,3,6];
            if (segmentsCount < 4) {
                return classNumbers[segmentSequence];
            } else {
                return segmentSequence;
            }
        };

        const _setScheduleGridRanges = (schedule, segments) => {
            const dayGrids = [
                schedule.sundayGridCells,
                schedule.mondayGridCells,
                schedule.tuesdayGridCells,
                schedule.wednesdayGridCells,
                schedule.thursdayGridCells,
                schedule.fridayGridCells,
                schedule.saturdayGridCells,
            ];
            dayGrids.forEach(dayGrid => dayGrid.forEach(dayGridCell => dayGridCell.inRange(0)));

            segments.forEach(segment => {
                let rangeStartCell = _scheduleCommonFunctions.getGridCell(segment.startHour(), segment.startMinute());
                let rangeEndCell = _scheduleCommonFunctions.getGridCell(segment.endHour(), segment.endMinute()) - 1;
                let rangeNumber = _rangeIndicatorClassNumber(schedule.segments().length, segment.sequence());

                [
                    {isForDay: segment.isForSunday(), dayGridCells: schedule.sundayGridCells},
                    {isForDay: segment.isForMonday(), dayGridCells: schedule.mondayGridCells},
                    {isForDay: segment.isForTuesday(), dayGridCells: schedule.tuesdayGridCells},
                    {isForDay: segment.isForWednesday(), dayGridCells: schedule.wednesdayGridCells},
                    {isForDay: segment.isForThursday(), dayGridCells: schedule.thursdayGridCells},
                    {isForDay: segment.isForFriday(), dayGridCells: schedule.fridayGridCells},
                    {isForDay: segment.isForSaturday(), dayGridCells: schedule.saturdayGridCells}
                ]
                    .forEach(({isForDay, dayGridCells}) => {
                        if (isForDay) {
                            for (let i = rangeStartCell; i <= rangeEndCell; i++) {
                                dayGridCells[i].inRange(rangeNumber);
                            }
                        }
                    });
            });
        };

        const _updateFilteredSchedules = () => {
            const filterContent = self.searchFilter();

            if (filterContent === '') {
                _state.filteredSchedules = _state.availableSchedules.slice();
                return;
            }

            const scheduleFilter = new FilterConstructor();
            scheduleFilter.addProperty('name');
            scheduleFilter.addValue(filterContent);
            _state.filteredSchedules = scheduleFilter.filter(_state.availableSchedules);
        };

        const _resetDisplayedSchedules = () => {
            self.lastPage = false;
            _state.schedulesShown = ROWS_PER_PAGE;
            self.displaySchedules(_state.filteredSchedules.slice(0, _state.schedulesShown));

            if (_state.schedulesShown >= _state.filteredSchedules.length) {
                self.lastPage = true;
            }
        };

        const _getNextBatchOfSchedules = (scrollEventSource) => {
            if (scrollEventSource !== self.scrollEventSource) {
                return;
            }

            if (self.lastPage === false && _getNextBatchCalled === false) {
                _getNextBatchCalled = true;

                const additionalSchedulesShown = _state.schedulesShown + ROWS_PER_PAGE;
                const additionalSchedules = _state.filteredSchedules.slice(_state.schedulesShown, additionalSchedulesShown);

                self.displaySchedules.push.apply(self.displaySchedules, additionalSchedules);
                _state.schedulesShown = additionalSchedulesShown;

                if (_state.schedulesShown >= _state.filteredSchedules.length) {
                    self.lastPage = true;
                }

                _getNextBatchCalled = false;
            }
        };

        const _onScheduleFilterChanged = () => {
            _updateFilteredSchedules();
            _resetDisplayedSchedules();
        };

        const _onSortTypeHandlerChanged = () => {
            const sorter = new SorterConstructor();
            if (sorter.setOptions(self.sortOptions(), self.sortType())) {
                sorter.sort(_state.availableSchedules);
                _updateFilteredSchedules();
                _resetDisplayedSchedules();
            }
        };

        const _showIntegrityWarningsPrompt = (scheduleId, integrityWarningPresentationObjects) => {
            _buildIntegrityWarningsModal(scheduleId);
            self.integrityWarningModal.addIntegrityWarnings(integrityWarningPresentationObjects);
            self.integrityWarningModal.showModal();
        };


        const _buildDeleteModal = (scheduleId) => {
            const displayType = _commonState.typeDisplay.schedule;
            let constructorParams = [scheduleId];
            self.actionModal
                .clearModal()
                .setContentViewModel(DeleteActionViewModelConstructor, constructorParams)
                .setHeaderText({i18n: {key: 'deleteHeader', options: {type: displayType}}})
                .setSubmitButtonText({i18n: 'delete'});
        };

        const _buildIntegrityWarningsModal = (scheduleId) => {
            const displayType = _commonState.typeDisplay.schedule;
            self.integrityWarningModal
                .clearModal()
                .setHeading({key: 'delete', options: {type: displayType}})
                .addText({i18n: {key: 'inUse', options: {type: displayType.toLowerCase()}}})
                .setSystemObject({objectId: scheduleId})
                .addIntegrityWarningText({i18n: {key: 'conflicts'}});
        };

        const _removeScheduleFromState = (scheduleId) => {
            self.displaySchedules.remove((schedule) => schedule.accountAttendantScheduleId === scheduleId);

            _state.availableSchedules = _state.availableSchedules.filter((schedule) => {
                return schedule.accountAttendantScheduleId !== scheduleId;
            });
            self.totalScheduleCount(_state.availableSchedules.length);

            _updateFilteredSchedules();

            _state.schedulesShown = self.displaySchedules().length;
            self.lastPage = _state.schedulesShown >= _state.filteredSchedules.length;

            if (self.displaySchedules().length < ROWS_PER_PAGE) {
                _getNextBatchOfSchedules(self.scrollEventSource);
            }
        };

        const _showDeletePrompt = (schedule) => {
            const scheduleId = schedule.accountAttendantScheduleId;
            _buildDeleteModal(scheduleId);
            self.actionModal.showModal()
                .done((result) => {
                    if (result === DELETE_RESPONSE) {
                        _facade.deleteSchedule(scheduleId)
                            .done(() => {
                                _removeScheduleFromState(scheduleId);
                            })
                            .fail((error) => {
                                if (error.constraintViolations && error.constraintViolations.length > 0) {
                                    _showIntegrityWarningsPrompt(scheduleId, error.constraintViolations);
                                } else {
                                    error.accountAttendantScheduleId = scheduleId;
                                    throw error;
                                }
                            });
                    }
                })
                .fail((error) => {
                    throw error;
                });
        };

        const _getSchedules = () => {
            _facade.getSchedules()
                .done((schedules) => {
                    let formattedSchedules = schedules.map((schedule) => {
                        schedule = Object.assign({}, schedule, {
                            sundayGridCells: _emptyGridCellsForDay(),
                            mondayGridCells: _emptyGridCellsForDay(),
                            tuesdayGridCells: _emptyGridCellsForDay(),
                            wednesdayGridCells: _emptyGridCellsForDay(),
                            thursdayGridCells: _emptyGridCellsForDay(),
                            fridayGridCells: _emptyGridCellsForDay(),
                            saturdayGridCells: _emptyGridCellsForDay(),
                            segments: ko.pureComputed(() => {
                                return schedule.segments.sort((a, b) => a.sequence() - b.sequence());
                            }),
                            get setGridRanges() {
                                return ko.computed(() => _setScheduleGridRanges(this, this.segments()));
                            }
                        });

                        return schedule;
                    });

                    _state.availableSchedules = formattedSchedules;
                    self.totalScheduleCount(_state.availableSchedules.length);

                    _onSortTypeHandlerChanged();
                    _updateFilteredSchedules();
                    _resetDisplayedSchedules();

                    if (_getNextBatchOfSchedulesEventId === null) {
                        _getNextBatchOfSchedulesEventId = _eventManager.subscribeBottomScrollBufferReached(_getNextBatchOfSchedules);
                    }
                });
        };

        self.actionModal = null;
        self.integrityWarningModal = null;
        self.isCompositionComplete = ko.observable(false);
        self.addScheduleUrl = _navigationConfiguration.routesById.newSchedule.url;
        self.lastPage = false;
        self.scrollEventSource = ScrollEventSources.settingsSchedules;

        self.displaySchedules = ko.observableArray([]);
        self.totalScheduleCount = ko.observable(0);

        self.sortOptions = ko.observableArray([
            {textI18n: "name", iconName: 'sortName', sortPath : "name", isAscending : true},
            {textI18n: "recentlyAdded", iconName: 'sortRecentlyAdded', sortPath : "createdDateTime.sortValue", isAscending : false},
            {textI18n: "recentlyModified", iconName: 'sortRecentlyModified', sortPath : "modifiedDateTime.sortValue", isAscending : false}
        ]);
        self.viewOptions = [
            {textI18n: 'expandAll', iconName: 'sortExpandAll', onClick: () => self.expandAllView()},
            {textI18n: 'collapseAll', iconName: 'sortCollapseAll', onClick: () => self.collapseAllView()},
        ];
        self.sortType = ko.observable("name");
        self.searchFilter = ko.observable('');

        _disposables.push(self.searchFilter.subscribe(_onScheduleFilterChanged));
        _disposables.push(self.sortType.subscribe(_onSortTypeHandlerChanged));

        self.showResults = ko.pureComputed(() => self.displaySchedules().length > 0);
        self.emptyStateContentKey = ko.pureComputed(() => self.totalScheduleCount() > 0 ? "noResults" : "noSchedules");

        self.expandAllView = () => {
            self.displaySchedules().forEach((schedule) => {
                schedule.showMap(true);
            });
        };

        self.collapseAllView = () => {
            self.displaySchedules().forEach((schedule) => {
                schedule.showMap(false);
            });
        };

        self.toggleMap = (schedule) => {
            schedule.showMap(!schedule.showMap());
            self.scheduleId = schedule.accountAttendantScheduleId;
        };

        self.launchDeleteSchedule = (schedule) => {
            const scheduleId = schedule.accountAttendantScheduleId;
            return _promiseFactory.deferIndefinitely((promise) => {
                _facade.getConstraints(scheduleId)
                    .done((integrityWarningPresentationObjects) => {
                        if (integrityWarningPresentationObjects.length > 0) {
                            _showIntegrityWarningsPrompt(scheduleId, integrityWarningPresentationObjects);
                        } else {
                            _showDeletePrompt(schedule);
                        }
                        promise.resolve();
                    });
            });
        };

        self.navigateToEditSchedule = (schedule) => {
            _router.navigate(schedule.editScheduleUrl);
        };


        self.scheduleMapDayLabels = _scheduleCommonFunctions.scheduleMapDayLabels;
        self.scheduleMapTimeLabels = _scheduleCommonFunctions.scheduleMapTimeLabels;

        self.compositionComplete = () => {
            self.isCompositionComplete(true);
        };

        self.detached = () => {
            _disposables.forEach(subscription => subscription.dispose());
            _disposables = [];

            if (_getNextBatchOfSchedulesEventId !== null) {
                _eventManager.unsubscribe(_getNextBatchOfSchedulesEventId);
                _getNextBatchOfSchedulesEventId = null;
            }
        };

        self.activate = () => {
            const FacadeConstructor = require('presentation/settings/schedules/facades/schedulesFacade');
            _facade = new FacadeConstructor();
            _facade.init(_promiseFactory);

            const ActionModalViewModelConstructor = require('presentation/common/actionModal/viewModels/actionModalViewModel');
            self.actionModal = new ActionModalViewModelConstructor();

            const IntegrityWarningModalViewModelConstructor = require('presentation/common/warningModal/viewModels/integrityWarningModalViewModel');
            self.integrityWarningModal = new IntegrityWarningModalViewModelConstructor();

            _eventManager = require('businessServices/events/eventManager');

            return _initialize();
        };

        const _initialize = () => {
            _getSchedules();

            return _promiseFactory.wait();
        };
    };
});

